import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Shield, Upload, CheckCircle2, Clock, XCircle, Loader2, FileText, Building2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InsurancePage = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [bankStatus, setBankStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [iban, setIban] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [bic, setBic] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [docsRes, bankRes] = await Promise.all([
          axios.get(`${API}/insurance/my`, { withCredentials: true }),
          axios.get(`${API}/bank-account/status`, { withCredentials: true })
        ]);
        setDocuments(docsRes.data || []);
        setBankStatus(bankRes.data);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Format non supporté. Utilisez PDF, JPEG ou PNG.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/insurance/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document soumis pour vérification');
      setDocuments(prev => [res.data, ...prev]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveBank = async () => {
    if (!iban.trim()) {
      toast.error('Veuillez saisir votre IBAN');
      return;
    }
    setSavingBank(true);
    try {
      const res = await axios.post(`${API}/bank-account/register`, {
        iban: iban.trim(),
        account_holder: accountHolder.trim() || user?.company_name || '',
        bic: bic.trim()
      }, { withCredentials: true });
      setBankStatus({ status: 'active', iban: res.data.iban, account_holder: res.data.account_holder });
      toast.success('Compte bancaire enregistré !');
      setIban('');
      setBic('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingBank(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified': return <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Vérifié</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejeté</Badge>;
      default: return <Badge className="bg-yellow-100 text-yellow-700 flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="insurance-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading font-bold text-3xl text-[#111827] mb-2">
          Vérifications & Paiements
        </h1>
        <p className="text-[#4B5563] mb-8">Gérez vos assurances et votre compte bancaire</p>

        {/* Bank Account IBAN */}
        <Card className="mb-6" data-testid="bank-account-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#1E3A8A]" />
              Compte bancaire - Recevoir des paiements
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bankStatus?.status === 'active' ? (
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-800">Compte enregistré</p>
                  <p className="text-sm text-emerald-600">IBAN: {bankStatus.iban}</p>
                  {bankStatus.account_holder && (
                    <p className="text-sm text-emerald-600">Titulaire: {bankStatus.account_holder}</p>
                  )}
                  <p className="text-xs text-emerald-500 mt-1">Les paiements escrow seront virés sur cet IBAN par virement SEPA</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-[#4B5563]">
                  Enregistrez votre IBAN pour recevoir les paiements par virement SEPA
                  lors de la libération des fonds escrow.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="account_holder" className="text-sm font-medium text-[#374151]">Titulaire du compte</Label>
                    <Input
                      id="account_holder"
                      placeholder={user?.company_name || 'Nom du titulaire'}
                      value={accountHolder}
                      onChange={(e) => setAccountHolder(e.target.value)}
                      data-testid="account-holder-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bic" className="text-sm font-medium text-[#374151]">BIC/SWIFT (optionnel)</Label>
                    <Input
                      id="bic"
                      placeholder="GEBABEBB"
                      value={bic}
                      onChange={(e) => setBic(e.target.value)}
                      data-testid="bic-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="iban" className="text-sm font-medium text-[#374151]">IBAN</Label>
                  <Input
                    id="iban"
                    placeholder="BE00 0000 0000 0000"
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    data-testid="iban-input"
                  />
                </div>
                <Button
                  className="btn-primary gap-2"
                  onClick={handleSaveBank}
                  disabled={savingBank}
                  data-testid="save-bank-button"
                >
                  {savingBank ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
                  {savingBank ? 'Enregistrement...' : 'Enregistrer mon IBAN'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Insurance Documents */}
        <Card data-testid="insurance-documents-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1E3A8A]" />
              Documents d'assurance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#4B5563] mb-4">
              Soumettez vos attestations d'assurance (RC Pro, Décennale) pour obtenir le badge "Assuré"
            </p>

            <label className="cursor-pointer mb-4 block">
              <input
                type="file"
                accept=".pdf,image/jpeg,image/png"
                onChange={handleUpload}
                className="hidden"
                data-testid="insurance-upload-input"
              />
              <div className="border-2 border-dashed border-[#1E3A8A]/20 rounded-lg p-4 text-center hover:border-[#1E3A8A]/40 transition-colors">
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1E3A8A]" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 mx-auto mb-2 text-[#6B7280]" />
                    <p className="text-sm text-[#6B7280]">Cliquez pour soumettre un document d'assurance</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PDF, JPEG ou PNG (max 20MB)</p>
                  </>
                )}
              </div>
            </label>

            {documents.length > 0 ? (
              <div className="space-y-3 mt-4">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-lg" data-testid={`insurance-doc-${doc.id}`}>
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#1E3A8A]" />
                      <div>
                        <p className="font-medium text-sm capitalize">{doc.type?.replace('_', ' ') || 'Document'}</p>
                        <p className="text-xs text-[#6B7280]">
                          Soumis le {new Date(doc.submitted_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(doc.status)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-[#6B7280] py-4">Aucun document soumis</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InsurancePage;
